import { DateString } from '@/types/state/filter';
import {
  getAllowedRange,
  getCurrentMonthDateRange,
  getCurrentQuarter,
  toIsoDateStringUtcCalendar,
  addDays,
  firstDayOfMonth,
  lastDayOfMonth,
  addMonths,
  today,
  getCurrentYear,
  getStartOfWeek,
} from '.';
import { DateRange } from '@/types';

/** A named date range. `value` is an inclusive `[from, to]` pair of `YYYY-MM-DD` strings. */
export interface DatePreset {
  name: string;
  value: [string, string];
}

interface DatePresetDates {
  name: string;
  value: DateRange;
}

/**
 * Returns the date range presets.
 */
export const getDatePresets = (): DatePreset[] => {
  const currentDate = today();
  const allowedDateRange = getAllowedRange();
  const yesterday = allowedDateRange.to;
  const currentQuarterNumber = getCurrentQuarter();
  const startOfThisWeekDateString = getStartOfWeek(currentDate);

  const currentYearNumber = Number(getCurrentYear());

  const quartersToDisplay = [...Array(currentQuarterNumber).keys()];

  const quarterPresets = quartersToDisplay.map((_, index) => {
    const quarterStartMonth = index * 3; // Q1=0, Q2=3, Q3=6, Q4=9

    const quarterStartDateString = toIsoDateStringUtcCalendar(
      new Date(Date.UTC(currentYearNumber, quarterStartMonth, 1))
    );

    // this looks odd, but JS will eg return 31st of December if you ask for Day 0 of Month 13.
    const quarterEndDateString = toIsoDateStringUtcCalendar(
      new Date(Date.UTC(currentYearNumber, index * 3 + 3, 0))
    );

    const finalEndDate = quarterEndDateString <= yesterday ? quarterEndDateString : yesterday;

    return {
      name: `Q${index + 1} this year`,
      value: { from: quarterStartDateString, to: finalEndDate },
    };
  });

  const isFirstOfMonth = currentDate.endsWith('01');

  const presets: (DatePresetDates | undefined)[] = [
    {
      name: 'Yesterday',
      value: { from: yesterday, to: yesterday },
    },
    {
      name: 'Last 7 days',
      value: { from: addDays(yesterday, -6), to: yesterday },
    },
    {
      name: 'Last week',
      value: {
        from: addDays(startOfThisWeekDateString, -7),
        to: addDays(startOfThisWeekDateString, -1),
      },
    },
    isFirstOfMonth
      ? undefined
      : {
          name: 'This month',
          value: getCurrentMonthDateRange(),
        },
    {
      name: 'Last 28 days',
      value: { from: addDays(yesterday, -27), to: yesterday },
    },
    {
      name: 'Last month',
      value: {
        from: firstDayOfMonth(addMonths(currentDate, -1)),
        to: lastDayOfMonth(addMonths(currentDate, -1)),
      },
    },
    {
      name: 'Last 6 months',
      value: { from: addMonths(currentDate, -6), to: yesterday },
    },
    {
      name: 'This year',
      value: {
        from: `${currentYearNumber}-01-01` as DateString,
        to: yesterday,
      },
    },
    {
      name: 'Last year',
      value: {
        from: `${currentYearNumber - 1}-01-01` as DateString,
        to: `${currentYearNumber - 1}-12-31` as DateString,
      },
    },
    ...quarterPresets,
  ];

  // filter out undefined item "This month" on the 1st
  return presets
    .filter((preset): preset is DatePresetDates => !!preset)
    .map(
      ({ name, value: { from, to } }): DatePreset => ({
        name,
        value: [from, to],
      })
    );
};

/** Finds the preset whose date range matches the given from/to ISO strings, if any. */
export const findMatchingPreset = (
  from: string,
  to: string,
  presets: DatePreset[] = getDatePresets()
): DatePreset | undefined =>
  presets.find(({ value: [start, end] }) => start === from && end === to);
